import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { buttonClass } from '@/components/generals/button'

import { siteConfig } from '@/constants/site'
import { NAV_LINKS, SHARE_LINK } from '@/constants/content'

const isActive = (pathname: string, href: string): boolean => {
    if (href === '/') return pathname === '/'
    if (href === '/tree') return pathname === '/tree' || pathname.startsWith('/people')

    return pathname.startsWith(href)
}

const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        setOpen(false)
    }, [router.asPath])

    useEffect(() => {
        if (!open) return

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [open])

    return (
        <header
            className={cn(
                'sticky top-0 z-50 border-b transition-colors duration-300',
                scrolled || open ? 'border-line bg-parchment/90 backdrop-blur-md' : 'border-transparent bg-parchment/0'
            )}
        >
            <div className='mx-auto flex h-18 max-w-6xl items-center justify-between gap-6 px-5 md:px-8'>
                <Link href='/' className='shrink-0 text-ink' aria-label={`${siteConfig.name}, home`}>
                    <Logo />
                </Link>

                <nav aria-label="Main" className='hidden md:block'>
                    <ul className='flex items-center gap-8'>
                        {NAV_LINKS.map(link => {
                            const active = isActive(router.pathname, link.href)

                            return (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        aria-current={active ? 'page' : undefined}
                                        className={cn(
                                            'relative py-2 text-[15px] font-semibold transition-colors duration-200',
                                            'after:absolute after:inset-x-0 after:-bottom-0.5 after:h-0.5 after:origin-left after:rounded-full after:bg-clay after:transition-transform after:duration-300',
                                            active ? 'text-ink after:scale-x-100' : 'text-umber after:scale-x-0 hover:text-ink hover:after:scale-x-100'
                                        )}
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                </nav>

                <div className='flex items-center gap-3'>
                    <Link
                        href={SHARE_LINK.href}
                        aria-current={router.pathname === SHARE_LINK.href ? 'page' : undefined}
                        className={buttonClass({ size: 'sm', className: 'hidden sm:inline-flex' })}
                    >
                        {SHARE_LINK.label}
                    </Link>

                    <button
                        type='button'
                        onClick={() => setOpen(value => !value)}
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        className='grid size-11 place-items-center rounded-full border border-ink/15 bg-linen text-ink md:hidden'
                    >
                        <span className='sr-only'>
                            {open ? "Close menu" : "Open menu"}
                        </span>
                        {open ? <X className='size-5' aria-hidden='true' /> : <Menu className='size-5' aria-hidden='true' />}
                    </button>
                </div>
            </div>

            {open && (
                <nav
                    id='mobile-menu'
                    aria-label="Mobile"
                    className='animate-in fade-in slide-in-from-top-2 border-t border-line bg-parchment px-5 pt-3 pb-6 duration-300 md:hidden'
                >
                    <ul className='space-y-1'>
                        {NAV_LINKS.map(link => {
                            const active = isActive(router.pathname, link.href)

                            return (
                                <li key={link.href}>
                                    <Link
                                        href={link.href}
                                        aria-current={active ? 'page' : undefined}
                                        className={cn(
                                            'block rounded-lg px-3 py-3 font-heading text-2xl',
                                            active ? 'bg-forest-soft text-forest-deep' : 'text-ink hover:bg-linen'
                                        )}
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>

                    <Link href={SHARE_LINK.href} className={buttonClass({ size: 'lg', className: 'mt-4 w-full' })}>
                        {SHARE_LINK.label}
                    </Link>
                </nav>
            )}
        </header>
    )
}

export default SiteHeader
